import React, { useState, useEffect, createContext, useContext } from 'react';
import { 
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut,
  onAuthStateChanged 
} from 'firebase/auth';
import { auth } from '../config/firebase.config';
import { actionCodeSettings } from '../config/firebase.config';
import sessionService, { INACTIVITY_TIMEOUT, WARNING_BEFORE_TIMEOUT } from '../services/session.service';
import { createAuditLog, AUDIT_ACTIONS } from '../services/audit.service';

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const FirebaseAuthGate = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loginStep, setLoginStep] = useState('email'); // 'email' or 'emailSent'
  const [userEmail, setUserEmail] = useState('');
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  
  useEffect(() => {
    // Set up session warning callback
    sessionService.setWarningCallback(() => {
      setShowSessionWarning(true);
    });
    
    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      setLoading(false);
      
      // Clear stored email if user is authenticated
      if (user) {
        window.localStorage.removeItem('emailForSignIn');
        setLoginStep('email');
        
        // Initialize session management
        await sessionService.initializeSession(user);
        
        // Log successful authentication
        await createAuditLog(AUDIT_ACTIONS.LOGIN, 'auth/login', {
          email: user.email,
          authMethod: 'passwordless',
          timestamp: new Date().toISOString()
        });
      }
    });
    
    // Check if this is a sign-in with email link
    if (isSignInWithEmailLink(auth, window.location.href)) {
      handleEmailLinkSignIn();
    }
    
    return () => {
      unsubscribe();
      // Cleanup session on unmount
      if (user) {
        sessionService.cleanup();
      }
    };
  }, []);
  
  const sendEmailLink = async (email) => {
    setError('');
    setLoading(true);
    
    try {
      console.log('🔍 Attempting to send sign-in link to:', email);
      console.log('📋 Action code settings:', actionCodeSettings);
      console.log('🔐 Auth domain:', auth.app.options.authDomain);
      console.log('🌐 Current URL:', window.location.href);
      console.log('⏰ Timestamp:', new Date().toISOString());
      
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      // Save email locally to complete sign-in on same device
      window.localStorage.setItem('emailForSignIn', email);
      setUserEmail(email);
      setLoginStep('emailSent');
      setLoading(false);
      
      console.log('✅ Email sent successfully to:', email);
    } catch (error) {
      console.error('❌ Error sending email link:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      // Enhanced error logging for debugging
      if (error.code === 'auth/unauthorized-domain') {
        console.error('🚨 Domain not authorized in Firebase Console');
        console.error('Add these domains to Firebase Console > Authentication > Settings > Authorized domains:');
        console.error('- localhost');
        console.error('- thegoatnote.com');
      }
      
      setError(getErrorMessage(error.code));
      setLoading(false);
    }
  };
  
  const handleEmailLinkSignIn = async () => {
    // Get email from storage or prompt user
    let email = window.localStorage.getItem('emailForSignIn');
    if (!email) {
      // User opened link on different device
      email = window.prompt('Please provide your email for confirmation');
    }
    
    if (email) {
      try {
        const result = await signInWithEmailLink(auth, email, window.location.href);
        // Clear email from storage
        window.localStorage.removeItem('emailForSignIn');
        // Clear the URL to remove the sign-in link
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (error) {
        console.error('Error completing sign-in:', error);
        setError(getErrorMessage(error.code));
      }
    }
  };
  
  const logout = async () => {
    try {
      // Terminate session first
      await sessionService.terminateSession('user_logout');
      // Then sign out from Firebase
      await signOut(auth);
      setUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
      setError('Failed to sign out');
    }
  };
  
  const extendSession = () => {
    setShowSessionWarning(false);
    sessionService.extendSession();
  };
  
  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/invalid-email':
        return 'Invalid email address';
      case 'auth/user-not-found':
        return 'No account found with this email';
      case 'auth/expired-action-code':
        return 'This sign-in link has expired. Please request a new one.';
      case 'auth/invalid-action-code':
        return 'This sign-in link is invalid. Please request a new one.';
      case 'auth/user-disabled':
        return 'This account has been disabled';
      default:
        return 'An error occurred. Please try again.';
    }
  };
  
  const value = {
    user,
    loading,
    error,
    sendEmailLink,
    logout,
    loginStep,
    userEmail,
    setLoginStep,
    extendSession
  };
  
  // If user is not authenticated, show login form
  if (!user && !loading) {
    return (
      <AuthContext.Provider value={value}>
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <h1 className="text-center text-3xl font-extrabold text-gray-900">
              GOATnote EHR
            </h1>
            <h2 className="mt-6 text-center text-2xl font-bold text-gray-900">
              {loginStep === 'email' ? 'Sign in to your account' : 'Check your email'}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {loginStep === 'email' 
                ? 'No password needed - we\'ll email you a secure link'
                : `We've sent a sign-in link to ${userEmail}`
              }
            </p>
          </div>
          
          <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
              {loginStep === 'email' ? (
                <EmailForm onSubmit={sendEmailLink} loading={loading} error={error} />
              ) : (
                <EmailSentConfirmation 
                  email={userEmail} 
                  onResend={() => sendEmailLink(userEmail)}
                  onChangeEmail={() => setLoginStep('email')}
                />
              )}
            </div>
          </div>
        </div>
      </AuthContext.Provider>
    );
  }
  
  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying credentials...</p>
        </div>
      </div>
    );
  }
  
  // User is authenticated, show app
  return (
    <AuthContext.Provider value={value}>
      {/* Session Warning Modal */}
      {showSessionWarning && (
        <SessionWarningModal 
          onExtend={extendSession}
          onLogout={logout}
        />
      )}
      {children}
    </AuthContext.Provider>
  );
};

// Email form component
const EmailForm = ({ onSubmit, loading, error }) => {
  const [email, setEmail] = useState('');
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (email) {
      onSubmit(email);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}
      
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
          Email address
        </label>
        <div className="mt-1">
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder="demo@thegoatnote.com"
          />
        </div>
      </div>
      
      <div>
        <button
          type="submit"
          disabled={loading || !email}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {loading ? 'Sending...' : 'Send sign-in link'}
        </button>
      </div>
      
      <div className="mt-6">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">Secure passwordless authentication</span>
          </div>
        </div>
      </div>
    </form>
  );
};

// Email sent confirmation component
const EmailSentConfirmation = ({ email, onResend, onChangeEmail }) => {
  return (
    <div className="text-center">
      <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
        <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      </div>
      
      <h3 className="mt-2 text-sm font-medium text-gray-900">Email sent!</h3>
      <p className="mt-1 text-sm text-gray-500">
        We've sent a sign-in link to:
      </p>
      <p className="mt-1 text-sm font-medium text-gray-900">{email}</p>
      
      <div className="mt-6 space-y-3">
        <button
          onClick={onResend}
          className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Resend email
        </button>
        
        <button
          onClick={onChangeEmail}
          className="w-full inline-flex justify-center py-2 px-4 text-sm font-medium text-blue-600 hover:text-blue-500"
        >
          Use a different email
        </button>
      </div>
      
      <div className="mt-6 text-xs text-gray-500">
        <p>Didn't receive the email?</p>
        <p className="mt-1">Check your spam folder or click resend.</p>
      </div>
    </div>
  );
};

// Session warning modal component
const SessionWarningModal = ({ onExtend, onLogout }) => {
  const [timeRemaining, setTimeRemaining] = useState(WARNING_BEFORE_TIMEOUT / 1000);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  return (
    <div className="fixed z-50 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
        
        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg className="h-6 w-6 text-yellow-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                  Session Timeout Warning
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    Your session will expire in <span className="font-bold text-yellow-600">{formatTime(timeRemaining)}</span> due to inactivity.
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Would you like to continue working?
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onExtend}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Continue Working
            </button>
            <button
              type="button"
              onClick={onLogout}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Log Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 